import { Link, usePage } from "@inertiajs/react";
import { useEffect, useState } from "react";
import ProductChangeExplainerDialog from "@/components/domains/ProductChangeExplainerDialog";
import Layout from "@/components/Layout";
import type { DomainIndexItem, SharedProps } from "@/types";
import { dismissProductChangeExplainer, isProductChangeExplainerDismissed } from "@/utils/productChangeExplainer";

type IndexProps = {
  domains: DomainIndexItem[];
  show_product_change_explainer: boolean;
};

export default function Index({ domains, show_product_change_explainer }: IndexProps) {
  const userId = usePage<SharedProps>().props.current_user?.id;
  const [explainerDismissed, setExplainerDismissed] = useState<boolean | null>(null);

  useEffect(() => {
    if (!show_product_change_explainer || !userId) {
      setExplainerDismissed(null);
      return;
    }

    setExplainerDismissed(isProductChangeExplainerDismissed(userId));
  }, [show_product_change_explainer, userId]);

  const dismissExplainer = () => {
    if (userId) {
      dismissProductChangeExplainer(userId);
    }
    setExplainerDismissed(true);
  };

  return (
    <Layout title="Domains">
      <div className="mb-6 flex flex-col items-start justify-between sm:flex-row sm:items-center">
        <div>
          <h1 className="mb-1 text-2xl font-semibold">Domains</h1>
          <p className="text-gray-500">Map pages and monitor UX issues from one website domain.</p>
        </div>

        <Link href="/domains/new" className="btn btn-primary mt-4 sm:mt-0">
          Add website
        </Link>
      </div>

      {domains.length === 0 ? (
        <div className="card">
          <div className="card-body text-center">
            <h2 className="mb-2 text-xl font-semibold">No websites added yet</h2>
            <p className="mb-6 text-gray-500">Start with one domain and we’ll discover pages from exploration.</p>
            <Link href="/domains/new" className="btn btn-primary">
              Add website
            </Link>
          </div>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="divide-y divide-gray-200">
            {domains.map((domain) => (
              <Link key={domain.id} href={`/domains/${domain.id}`} className="block p-5 hover:bg-gray-50">
                <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                  <div>
                    <h2 className="font-semibold">{domain.hostname}</h2>
                    <p className="text-sm text-gray-500">{domain.url}</p>
                  </div>
                  <div className="text-sm text-gray-500">
                    {domain.page_count} pages
                    {domain.latest_audit_status ? ` - ${domain.latest_audit_status}` : ""}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <ProductChangeExplainerDialog
        open={show_product_change_explainer && explainerDismissed === false}
        onDismiss={dismissExplainer}
      />
    </Layout>
  );
}
